import React from 'react';
import BaseComponent from 'meta/features/base-component';
import WebEvents from 'meta/features/web-events';
import ErrorLogger from "meta/utils/logError";
import { v4 as createUuid } from "uuid";
import { setDataGlobal } from 'meta/features/json-params';
import { setupUserActivityChecking } from 'meta/features/user-activity';
import { addQueryParameterToUri } from 'meta/utils/uri';
import { getRootAppWindow } from 'meta/utils/getRootAppWindow';
import { getRootApi } from 'meta/utils/getRootApi';
function setupErrorLogging(win) {
    win.addEventListener("error", ErrorLogger);
}
class Iframe extends BaseComponent {
    static defaultProps = {
        src: '',
        onLoad: () => { },
        onAsyncLoad: () => { },
        onBeginRequest: () => { },
        onEndRequest: () => { },
        onWebEvent: () => { },
        name: createUuid(),
        features: {
            errorLoggingEnabled: true,
            uiEnabled: true,
            activityLoggingEnabled: true,
            jsonParamsEnabled: true,
            webEventsEnabled: true
        }
    };
    constructor(props) {
        super(props);
        this._bind('getUrl', 'handleLoad');
        this.state = { LastActiveElement: null, ContentWindow: null, LastScroll: null };
    }
    setLastActiveElement(element) {
        this.setState({
            LastActiveElement: element
        });
    }
    handleFocus() {
        if (this.props.isActive) {
            if (this.state.LastActiveElement) {
                if (this.state.ContentWindow && this.state.ContentWindow.document.body == this.state.LastActiveElement) {
                    this.state.ContentWindow.focus();
                }
                else if (this.state.LastActiveElement instanceof HTMLElement) {
                    this.state.LastActiveElement.focus();
                }
            }
        }
    }
    setLastScrollPosition(positionY) {
        if (!this.state.LastScroll || positionY) {
            this.setState({
                LastScroll: positionY
            });
        }
    }
    handleScrollPos() {
        if (this.props.isActive && this.state.ContentWindow && this.state.LastScroll && !this.state.ContentWindow.scrollY) {
            this.state.ContentWindow.scrollTo(0, this.state.LastScroll);
        }
    }
    getUrl() {
        let { src, name } = this.props;
        let frameSession = this.props.frameSession;
        if (!src)
            return undefined;
        if (!frameSession)
            frameSession = getRootAppWindow(window).RootAPI.FrameSessionResolver.resolve(name);
        if (!frameSession || src.indexOf('frameSession') !== -1)
            return src;
        return addQueryParameterToUri(src, 'frameSession', frameSession);
    }
    handleLoad(e) {
        let { onLoad, onAsyncLoad, onEndRequest, onWebEvent, features, onMessage, name, onModalWebEvent } = this.props;
        let _features = Object.assign({}, Iframe.defaultProps.features, features);
        const { contentWindow } = e.currentTarget;
        if (contentWindow === null) {
            throw Error("No window present on load of iframe");
        }
        // setup activity checking
        if (_features.activityLoggingEnabled)
            setupUserActivityChecking(contentWindow);
        // bind parent error handler to keep info about javascript errors.
        if (_features.errorLoggingEnabled && !contentWindow.initLoaded)
            setupErrorLogging(contentWindow);
        // bind embeded data to JsonParams object on window
        if (_features.jsonParamsEnabled)
            setDataGlobal(contentWindow);
        // run WebEvents registered handlers
        if (_features.webEventsEnabled) {
            const handleWebEvent = function (event) {
                getRootApi().FrontendEvents.webEvent(event, name);
            };
            // run on sync postback
            WebEvents.run(contentWindow, handleWebEvent);
            // run on async postback
            if (contentWindow.Sys && contentWindow.Sys.Application) {
                contentWindow.Sys.Application.add_load(function () {
                    setDataGlobal(contentWindow);
                    WebEvents.run(contentWindow, handleWebEvent);
                });
            }
        }
        // setup global UI API window
        if (_features.uiEnabled && !contentWindow.RootAPI) {
            var win = window;
            var levelsUpCount = 0;
            while (win.RootAPI === undefined && levelsUpCount < 10) {
                win = win.parent;
                levelsUpCount++;
            }
            contentWindow.RootAPI = win.RootAPI;
        }
        this.setLastActiveElement(contentWindow.document.activeElement);
        this.setState({
            ContentWindow: contentWindow
        });
        const handleFocusIn = () => this.setLastActiveElement(contentWindow.document.activeElement);
        contentWindow.addEventListener('focusin', handleFocusIn, true);
        const handleScroll = () => this.setLastScrollPosition(contentWindow.scrollY);
        contentWindow.addEventListener('scroll', handleScroll, true);
        // Handling message broadcast
        contentWindow.addEventListener('message', (m) => {
            if (onMessage)
                onMessage({
                    ...m.data,
                    originalMessage: m
                });
            const { type } = m.data;
            if (type === 'TAB_SWITCH') {
                if (m.data.newTabKey === contentWindow.ParentWindow.name) {
                    this.handleFocus();
                    this.handleScrollPos();
                }
            }
            if (type === 'MODAL_CLOSED' && window.RootAPI.Navigation.getCurrentIframe().name === contentWindow.ParentWindow.name) {
                this.handleFocus();
            }
            if (type === 'WEB_EVENT') {
                const { event, sourceIframeName } = m.data;
                console.debug(`WEB_EVENT handled in frame:${name}; sourceIframe:${sourceIframeName};event type is:${event.type}`);
                if (sourceIframeName === name) {
                    onWebEvent(event);
                }
                if (contentWindow.DialogManager) {
                    let dialog = contentWindow.DialogManager.LocalDialog.getCurrent();
                    if (dialog && dialog.config && dialog.config.iframeName === sourceIframeName) {
                        if (onModalWebEvent) {
                            onModalWebEvent(event);
                        }
                    }
                }
            }
        });
        contentWindow.addEventListener('click', e => {
            getRootApi().FrontendEvents.click(contentWindow.name);
        });
        contentWindow.addEventListener('pagehide', () => {
            contentWindow.removeEventListener('focusin', handleFocusIn, true);
            contentWindow.removeEventListener('scroll', handleScroll, true);
        });
        if (contentWindow.Sys && contentWindow.Sys.Application) {
            contentWindow.Sys.Application.add_load(function () {
                onAsyncLoad(contentWindow);
            });
        }
        onLoad(e, contentWindow);
        onEndRequest();
    }
    componentDidMount() {
        let { onBeginRequest } = this.props;
        onBeginRequest();
    }
    componentDidUpdate(prevProps) {
        let { onBeginRequest, src } = this.props;
        if (prevProps.src !== src) {
            onBeginRequest();
        }
        if (prevProps.isActive !== this.props.isActive) {
            this.handleFocus();
            this.handleScrollPos();
        }
    }
    render() {
        let { src, onLoad, onAsyncLoad, onBeginRequest, onEndRequest, onWebEvent, onMessage, features, onRef, onModalWebEvent, isActive, ...restProps } = this.props;
        return (<iframe title="iframe" src={this.getUrl()} onLoad={this.handleLoad} ref={onRef} {...restProps}/>);
    }
}
export default Iframe;
